<script setup lang="ts">
import IconChevron from '@/components/icons/IconChevron.vue'
import { useChatMessageSendingJobDetailPageQuery } from './ChatMessageSendingJobDetailPage.generated'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import ChatMessageSendingJobIndexPageSideMenu from '@/pages/ChatMessageSendingJobIndexPage/ChatMessageSendingJobIndexPageSideMenu.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import HelpPoptip from '@/components/molecules/HelpPoptip.vue'
import { ChatMessageSendingJobStatus } from '@/types/schema.generated'
import { format } from 'date-fns'
import { fromTimeString } from '@/libs/utilities/gql-scalar'
import ChatMessageSendingJobDetailPageScheduledTime from './ChatMessageSendingJobDetailPageScheduledTime/ChatMessageSendingJobDetailPageScheduledTime.vue'
import ChatMessageSendingJobDetailPageDeleteButton from './ChatMessageSendingJobDetailPageDeleteButton/ChatMessageSendingJobDetailPageDeleteButton.vue'

const route = useRoute('chatMessageSendingJobDetail')

const jobGuid = computed(() => {
  const value = route.params.jobGuid
  return Array.isArray(value) ? value[0] : value
})

const pageQuery = useChatMessageSendingJobDetailPageQuery({
  guid: jobGuid.value,
})

const queryResult = computed(() => {
  return pageQuery.result.value
})

const overviewDeliveredCountDisplay = computed(() => {
  const deliveredCount =
    queryResult.value?.chatMessageSendingJob?.deliveredCount
  return deliveredCount == null ? '-' : deliveredCount.toLocaleString() + '人'
})

const overviewUniqueImpressionDisplay = computed(() => {
  const uniqueImpression =
    queryResult.value?.chatMessageSendingJob?.statistics?.overview
      ?.uniqueImpression
  return uniqueImpression == null
    ? '-'
    : uniqueImpression.toLocaleString() + '人'
})

const messageSeq = computed(() => {
  const statisticsMessages =
    queryResult.value?.chatMessageSendingJob?.statistics?.message || []
  const statisticsClicks =
    queryResult.value?.chatMessageSendingJob?.statistics?.click || []
  return queryResult.value?.chatMessageSendingJob?.messages.map(
    (message, index) => {
      const statMessage = statisticsMessages[index]
      const statClicks = statisticsClicks.filter(
        statClick => statClick.seq === statMessage?.seq
      )
      return {
        ...message,
        uniqueImpression:
          statMessage?.uniqueImpression == null
            ? '-'
            : statMessage.uniqueImpression.toLocaleString() + '人',
        uniqueClicks:
          statClicks.length === 0
            ? []
            : statClicks.map(clickStat => ({
                url: clickStat.url,
                uniqueClick:
                  clickStat.uniqueClick == null
                    ? '-'
                    : clickStat.uniqueClick.toLocaleString() + '人',
              })),
        seq: statMessage?.seq,
      }
    }
  )
})

const status = computed(() => {
  return queryResult.value?.chatMessageSendingJob?.status
})

const statusText = computed(() => {
  switch (status.value) {
    case ChatMessageSendingJobStatus.ChatMessageSendingJobStatusCompleted:
      return '完了'
    case ChatMessageSendingJobStatus.ChatMessageSendingJobStatusRunning:
      return '実行中'
    case ChatMessageSendingJobStatus.ChatMessageSendingJobStatusPending:
      return '予約済'
    case ChatMessageSendingJobStatus.ChatMessageSendingJobStatusFailed:
      return '失敗'
  }
  return '完了'
})

const formatNullableTimeString = (
  time: TimeString | null | undefined
): string => {
  if (!time) {
    return '-'
  }

  return format(fromTimeString(time), 'yyyy/MM/dd HH:mm')
}
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <ChatMessageSendingJobIndexPageSideMenu />
      <div :class="$style.wrapper" v-if="queryResult?.chatMessageSendingJob">
        <section :class="$style.container">
          <header :class="$style.header">
            <h1>LINEメッセージ配信</h1>
            <ChatMessageSendingJobDetailPageDeleteButton
              :fragment="queryResult"
            />
          </header>
          <hr :class="$style.hr" />
          <div :class="$style.containerInner">
            <div :class="$style.metadata">
              <div>
                <dt>配信状況</dt>
                <dd>
                  {{ statusText }}
                </dd>
              </div>
              <div>
                <dt>配信設定日時</dt>
                <dd>
                  <ChatMessageSendingJobDetailPageScheduledTime
                    :fragment="queryResult"
                    :refetch="
                      async () => {
                        await pageQuery.refetch()
                      }
                    "
                  />
                </dd>
              </div>
              <div>
                <dt>配信完了日時</dt>
                <dd>
                  {{
                    formatNullableTimeString(
                      queryResult.chatMessageSendingJob.sentAt
                    )
                  }}
                </dd>
              </div>
              <div>
                <dt>ターゲット</dt>
                <dd>{{ queryResult.chatMessageSendingJob.target }}</dd>
              </div>
              <div>
                <dt>
                  {{
                    status ===
                    ChatMessageSendingJobStatus.ChatMessageSendingJobStatusCompleted
                      ? '配信された人数'
                      : '配信される人数'
                  }}
                </dt>
                <p :class="$style.termNote">
                  ※反映に時間がかかる場合があります
                </p>
                <dd>{{ overviewDeliveredCountDisplay }}</dd>
              </div>
              <div>
                <div :class="$style.row">
                  <dt>開封した人数</dt>
                  <HelpPoptip :class="$style.title__poptip">
                    <p :class="[$style.tips, $style['tips--content']]">
                      開封した人数が20人未満の場合は、LINEの仕様により集計結果が取得できません。
                      開封した人数は、配信日から14日間のみ更新されます。
                    </p>
                  </HelpPoptip>
                </div>
                <p :class="$style.termNote">
                  ※反映に時間がかかる場合があります
                </p>
                <dd>{{ overviewUniqueImpressionDisplay }}</dd>
              </div>
            </div>
            <hr :class="$style.hr" />
            <div>
              <div
                v-for="(m, i) of messageSeq"
                :key="m.guid"
                :class="$style.messageArea"
              >
                <p :class="$style.messageHeading">メッセージ{{ i + 1 }}</p>
                <dl v-if="m.__typename === 'ChatMessageSendingJobMessageText'">
                  <div>
                    <dt>種類</dt>
                    <dd>テキスト</dd>
                  </div>
                  <div>
                    <dt>本文</dt>
                    <dd :class="$style.messageText">
                      {{ m.text }}
                    </dd>
                  </div>
                  <div>
                    <div :class="[$style.row]">
                      <dt>開封した人数</dt>
                      <HelpPoptip :class="$style.title__poptip">
                        <p :class="[$style.tips, $style['tips--content']]">
                          開封した人数が20人未満の場合は、LINEの仕様により集計結果が取得できません。
                          開封した人数は、配信日から14日間のみ更新されます。
                        </p>
                      </HelpPoptip>
                    </div>
                    <p :class="$style.termNote">
                      ※反映に時間がかかる場合があります
                    </p>
                    <dd :class="$style.messageText">
                      {{ m.uniqueImpression }}
                    </dd>
                  </div>
                  <div>
                    <div :class="[$style.row]">
                      <dt>URLクリック人数</dt>
                      <HelpPoptip :class="$style.title__poptip">
                        <p :class="[$style.tips, $style['tips--content']]">
                          クリック人数が20人未満の場合は、LINEの仕様により集計結果が取得できません。
                          クリック人数は、配信日から14日間のみ更新されます。
                        </p>
                      </HelpPoptip>
                    </div>
                    <p :class="$style.termNote">
                      ※反映に時間がかかる場合があります
                    </p>
                    <dd>
                      <table
                        v-if="m.uniqueClicks.length > 0"
                        :class="$style.clickTable"
                      >
                        <thead>
                          <tr>
                            <th>URL</th>
                            <th>人数</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(click, index) in m.uniqueClicks"
                            :key="index"
                          >
                            <td>
                              {{ click.url }}
                            </td>
                            <td>{{ click.uniqueClick }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <span v-else>-</span>
                    </dd>
                  </div>
                </dl>
                <dl v-if="m.__typename === 'ChatMessageSendingJobMessageImage'">
                  <div>
                    <dt>種類</dt>
                    <dd>画像</dd>
                  </div>
                  <div>
                    <dt>画像</dt>
                    <dd>
                      <img
                        :src="m.imageUri"
                        :alt="m.altText"
                        :class="$style.messageImage"
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>リンクURL</dt>
                    <dd>
                      <ExternalLink :href="m.linkUri">
                        {{ m.linkUri }}
                      </ExternalLink>
                    </dd>
                  </div>
                  <div>
                    <dt>代替テキスト</dt>
                    <dd>{{ m.altText }}</dd>
                  </div>
                </dl>
                <dl
                  v-if="m.__typename === 'ChatMessageSendingJobMessageQuestion'"
                >
                  <div>
                    <dt>種類</dt>
                    <dd>選択肢つきテキスト</dd>
                  </div>
                  <div>
                    <dt>本文</dt>
                    <dd :class="$style.messageText">
                      {{ m.body }}
                    </dd>
                  </div>
                  <div>
                    <dt>選択肢</dt>
                    <dd>
                      <ul>
                        <li
                          v-for="s of m.selections"
                          :key="s"
                          :class="$style.selectionLi"
                        >
                          {{ s }}
                        </li>
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>
        <div>
          <RouterLink
            :to="{ name: 'chatMessageSendingJobIndex' }"
            :class="$style.back"
          >
            <IconChevron direction="left" />
            一覧に戻る
          </RouterLink>
        </div>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;

  > div {
    width: calc(50% - 32px / 2);
  }

  dt {
    font-size: 16px;
    font-weight: bold;
  }

  .termNote {
    color: $color-text-and-object-low-emphasis;
    font-weight: normal;
  }

  dd {
    margin-top: 8px;
  }
}

.messageHeading {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.messageArea {
  &:not(:first-child) {
    margin-top: 32px;
  }

  dl {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  dt {
    font-size: 16px;
    font-weight: bold;
  }

  dd {
    margin-top: 8px;
  }

  .termNote {
    color: $color-text-and-object-low-emphasis;
    font-weight: normal;
  }
}

.messageText {
  white-space: pre-wrap;
}

.messageImage {
  width: 80%;
}

.selectionLi {
  list-style: disc;
  padding-left: 8px;
  margin-left: 16px;
}

.back {
  display: inline-flex;
  align-items: center;
}

.setting {
  display: grid;
  align-items: flex-start;
  gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}

.row {
  display: flex;
}

.tips {
  overflow-wrap: break-word;

  &--content {
    width: 200px;
  }
}

.title {
  display: flex;
  align-items: center;
  background-color: $color-background-base;
  padding: 0 0 0 24px;

  &__poptip {
    margin-left: 8px;
  }
}

.clickTable {
  border-collapse: collapse;
  margin-top: 8px;

  th,
  td {
    border: 1px solid $color-border-low-emphasis;
    padding: 8px;
    text-align: left;
    word-break: break-all;
  }

  th {
    background-color: $color-background-base;
    font-weight: bold;
  }

  td a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  th:nth-child(2) {
    text-align: center;
    width: 100px;
  }

  td:nth-child(2) {
    text-align: center;
    width: 100px;
  }
}
</style>
